/**
 * Rate Limiter Utility
 * 
 * This utility provides functions to track and limit message attempts that contain prohibited content.
 * It helps prevent users from repeatedly trying to bypass content filters.
 */

// In-memory store for tracking user attempts
// In a production environment, this should be moved to a database or Redis
const userAttempts = {};

// Constants for rate limiting
const RATE_LIMIT_WINDOW = 10 * 60 * 1000; // 10 minutes in milliseconds
const MAX_PROHIBITED_ATTEMPTS = 3; // Maximum allowed prohibited content attempts within window
const COOLDOWN_PERIOD = 30 * 60 * 1000; // 30 minutes cooldown after exceeding limit

/**
 * Record a prohibited content attempt for a user
 * @param {string} userId - The user ID
 * @returns {object} - Status of the user's rate limit
 */
export const recordProhibitedAttempt = (userId) => {
  const now = Date.now();
  
  // Initialize user entry if it doesn't exist
  if (!userAttempts[userId]) {
    userAttempts[userId] = {
      attempts: [],
      blockedUntil: null
    };
  }
  
  const userRecord = userAttempts[userId];
  
  // If user is currently blocked, check if block period has expired
  if (userRecord.blockedUntil && userRecord.blockedUntil > now) {
    return {
      isBlocked: true,
      remainingTime: Math.ceil((userRecord.blockedUntil - now) / 1000 / 60), // minutes remaining
      attemptsCount: userRecord.attempts.length
    };
  } else if (userRecord.blockedUntil) {
    // Block period has expired, reset attempts
    userRecord.attempts = [];
    userRecord.blockedUntil = null;
  }
  
  // Filter out attempts older than the rate limit window
  userRecord.attempts = userRecord.attempts.filter(
    timestamp => timestamp > now - RATE_LIMIT_WINDOW
  );
  
  // Add the current attempt
  userRecord.attempts.push(now);
  
  // Check if user has exceeded the maximum allowed attempts
  if (userRecord.attempts.length > MAX_PROHIBITED_ATTEMPTS) {
    userRecord.blockedUntil = now + COOLDOWN_PERIOD;
    return {
      isBlocked: true,
      remainingTime: Math.ceil(COOLDOWN_PERIOD / 1000 / 60), // minutes
      attemptsCount: userRecord.attempts.length
    };
  }
  
  return {
    isBlocked: false,
    attemptsCount: userRecord.attempts.length,
    attemptsRemaining: MAX_PROHIBITED_ATTEMPTS - userRecord.attempts.length
  };
};

/**
 * Check if a user is currently rate limited
 * @param {string} userId - The user ID
 * @returns {object} - Status of the user's rate limit
 */
export const checkRateLimit = (userId) => {
  const now = Date.now();
  
  // If no record exists, user is not rate limited
  if (!userAttempts[userId]) {
    return {
      isBlocked: false,
      attemptsCount: 0,
      attemptsRemaining: MAX_PROHIBITED_ATTEMPTS
    };
  }
  
  const userRecord = userAttempts[userId];
  
  // If user is currently blocked, check if block period has expired
  if (userRecord.blockedUntil && userRecord.blockedUntil > now) {
    return {
      isBlocked: true,
      remainingTime: Math.ceil((userRecord.blockedUntil - now) / 1000 / 60), // minutes remaining
      attemptsCount: userRecord.attempts.length
    };
  } else if (userRecord.blockedUntil) {
    // Block period has expired, reset attempts
    userRecord.attempts = [];
    userRecord.blockedUntil = null;
  }
  
  // Filter out attempts older than the rate limit window
  userRecord.attempts = userRecord.attempts.filter(
    timestamp => timestamp > now - RATE_LIMIT_WINDOW
  );
  
  return {
    isBlocked: false,
    attemptsCount: userRecord.attempts.length,
    attemptsRemaining: MAX_PROHIBITED_ATTEMPTS - userRecord.attempts.length
  };
};

/**
 * Clear rate limit data for a user (e.g., after successful admin review)
 * @param {string} userId - The user ID to clear rate limit for
 */
export const clearRateLimit = (userId) => {
  if (userAttempts[userId]) {
    delete userAttempts[userId];
  }
};
